import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, User, ChevronDown, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { AREAS, COLLECTIONS } from "@/lib/constants";

const NavLink = ({ to, children, light }) => (
  <Link
    to={to}
    className={`text-sm font-medium transition-colors ${light ? "text-white/90 hover:text-white" : "text-foreground/80 hover:text-foreground"}`}
  >
    {children}
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const { t, localePath, locale } = useLanguage();
  const navigate = useNavigate();
  const isHome = window.location.pathname === "/" || window.location.pathname === `/${locale}`;
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = transparent;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-[var(--lux-ivory)]/95 backdrop-blur-md border-b border-border"
      }`}
    >
      <div className="container-lux flex h-[68px] items-center justify-between gap-4">
        <Link to={localePath("/")} className="flex items-center gap-2" data-testid="navbar-logo">
          <span className={`font-display text-xl tracking-tight ${light ? "text-white" : "text-foreground"}`}>
            MARBELLA
          </span>
          <span className={`text-[10px] uppercase tracking-[0.3em] mt-1 ${light ? "text-white/70" : "text-muted-foreground"}`}>
            Royal Villas
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavLink to={localePath("/search")} light={light}>{t("nav.villas")}</NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors ${light ? "text-white/90 hover:text-white" : "text-foreground/80 hover:text-foreground"}`} data-testid="nav-areas-trigger">
                {t("nav.areas")} <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {AREAS.map((a) => (
                <DropdownMenuItem key={a.slug} onClick={() => navigate(localePath(`/areas/${a.slug}`))} data-testid={`nav-area-${a.slug}`}>
                  <MapPin className="h-3.5 w-3.5 mr-2 text-[var(--lux-seaglass)]" /> {a.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors ${light ? "text-white/90 hover:text-white" : "text-foreground/80 hover:text-foreground"}`} data-testid="nav-collections-trigger">
                {t("nav.collections")} <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {COLLECTIONS.map((c) => (
                <DropdownMenuItem key={c.slug} onClick={() => navigate(localePath(`/collections/${c.slug}`))}>
                  {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to={localePath("/blog")} light={light}>{t("nav.guide")}</NavLink>
          <NavLink to={localePath("/about")} light={light}>{t("nav.about")}</NavLink>
          <NavLink to={localePath("/contact")} light={light}>{t("nav.contact")}</NavLink>
          <NavLink to={localePath("/booking-status")} light={light}>{t("bookingStatus.nav")}</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher light={light} data-testid="navbar-language-switcher" />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="navbar-account-button" className={`flex items-center gap-2 text-sm font-medium ${light ? "text-white" : "text-foreground"}`}>
                  <User className="h-4 w-4" /> {user.name.split(" ")[0]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard">{t("nav.myDashboard")}</DropdownMenuItem>
                {user.role === "owner" && <DropdownMenuItem onClick={() => navigate("/owner")}>{t("nav.ownerPortal")}</DropdownMenuItem>}
                {(user.role === "admin" || user.role === "manager") && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin">{t("nav.adminPanel")}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate(localePath("/")); }} data-testid="nav-logout">{t("nav.signOut")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="navbar-signin-button"
              className={light ? "text-white hover:bg-white/10 hover:text-white" : ""}>
              {t("nav.signIn")}
            </Button>
          )}
          <Button onClick={() => navigate(localePath("/search"))} data-testid="navbar-booking-cta-button"
            className="bg-[var(--lux-ink)] text-[var(--lux-ivory)] hover:bg-black rounded-[12px]">
            {t("nav.findVilla")}
          </Button>
        </div>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher light={light} />
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="navbar-mobile-menu-button" className={`p-2 ${light ? "text-white" : "text-foreground"}`}>
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-[var(--lux-ivory)]">
              <div className="flex flex-col gap-1 mt-8">
                <SheetClose asChild><Link to={localePath("/search")} className="py-3 text-lg font-display border-b border-border">{t("nav.villas")}</Link></SheetClose>
                <div className="py-2 text-xs uppercase tracking-widest text-muted-foreground mt-2">{t("nav.areas")}</div>
                {AREAS.map((a) => (
                  <SheetClose asChild key={a.slug}><Link to={localePath(`/areas/${a.slug}`)} className="py-2 text-sm">{a.name}</Link></SheetClose>
                ))}
                <div className="py-2 text-xs uppercase tracking-widest text-muted-foreground mt-2">{t("footer.guide")}</div>
                <SheetClose asChild><Link to={localePath("/blog")} className="py-2 text-sm">{t("nav.guide")}</Link></SheetClose>
                <SheetClose asChild><Link to={localePath("/about")} className="py-2 text-sm">{t("nav.about")}</Link></SheetClose>
                <SheetClose asChild><Link to={localePath("/contact")} className="py-2 text-sm">{t("nav.contact")}</Link></SheetClose>
                <SheetClose asChild><Link to={localePath("/booking-status")} className="py-2 text-sm">{t("bookingStatus.nav")}</Link></SheetClose>
                <div className="mt-4 flex flex-col gap-2">
                  {user ? (
                    <>
                      <SheetClose asChild><Link to="/dashboard"><Button variant="outline" className="w-full">{t("nav.myDashboard")}</Button></Link></SheetClose>
                      {(user.role === "admin" || user.role === "manager") && <SheetClose asChild><Link to="/admin"><Button variant="outline" className="w-full">{t("nav.adminPanel")}</Button></Link></SheetClose>}
                      <Button onClick={() => { logout(); navigate(localePath("/")); }} variant="ghost" className="w-full">{t("nav.signOut")}</Button>
                    </>
                  ) : (
                    <SheetClose asChild><Link to="/login"><Button variant="outline" className="w-full">{t("nav.signIn")}</Button></Link></SheetClose>
                  )}
                  <SheetClose asChild><Link to={localePath("/search")}><Button className="w-full bg-[var(--lux-ink)] text-[var(--lux-ivory)]">{t("nav.findVilla")}</Button></Link></SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
